import { css } from '@emotion/react';
import React from 'react';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import { getCourseRolePlays } from 'redux/actions/ai-role-play';
import { getFeatureableExercises } from 'redux/actions/exercises';
import { getCourseScenarios } from 'redux/actions/video-practice';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import NvDropdown, {
  NvDropdownAlign,
  NvDropdownButtonStyle,
  NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import { useCourseHomeUtils } from 'shared/hooks/use-course-home-utils';
import LoadingSpinner from 'shared/components/loading-spinner';
import { black, gray2, warning, white } from 'styles/global_defaults/colors';
import { dropdownZIndex, standardSpacing } from 'styles/global_defaults/scaffolding';
import { VideoPracticeScenario } from 'redux/schemas/models/video-practice';
import { CourseExercise } from 'redux/schemas/models/exercise';

type AiRolePlay = {
  id: number;
  title: string;
};

type Props = {
  /**
   * Renders the component as an inline mobile gallery panel.
   * When true: data is fetched on mount instead of lazily on dropdown open,
   * the dropdown is forced open so accordion items are immediately visible,
   * and the desktop icon/trigger is hidden.
   * Used by MobileSubmissionsGalleryMenu in nv-header-page-links when the
   * user taps "Gallery" in the mobile three-dot nav.
   */
  isMobileMenu?: boolean;
};

const styles = css`
  height: inherit;

  button {
    padding: 0;
    border: none;
  }

  .bs4-dropdown-toggle::after {
    height: inherit;
    content: none;
    display: none;
  }

  &.is-white-icon .icon-wrapper,
  .is-white-icon .icon-wrapper {
    color: ${white};
  }

  .nv-dropdown,
  .bs4-dropdown,
  .nv-dropdown [role='button'] {
    height: inherit;
  }

  .bs4-dropdown-menu {
    width: 220px;
    max-width: 220px;

    .bs4-dropdown-item {
      color: ${black};
      white-space: initial;
      line-height: ${standardSpacing}px;
    }

    .accordion-header {
      i.icon {
        color: ${gray2};
      }

      &.expanded i.icon {
        color: ${warning};
      }
    }
  }
  .submissions-gallery-menu {
    max-height: 300px;
    overflow-y: auto;
    z-index: ${dropdownZIndex + 1};
  }
`;

const loadingItemStyle = css`min-height: 110px`;

const SubmissionsGalleryMenu = ({ isMobileMenu = false }: Props) => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state, $stateParams] = injectServices(['$state', '$stateParams']);
  const { isWhiteIcon, isEditOrReorderMode } = useCourseHomeUtils();
  const dispatch = useAppDispatch();

  const [featureableExercises, setFeatureableExercises] = React.useState<CourseExercise[]>([]);
  const [aiRolePlays, setAiRolePlays] = React.useState<AiRolePlay[]>([]);
  const [scenarios, setScenarios] = React.useState<VideoPracticeScenario[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const hasFetched = React.useRef(false);
  const isFetching = React.useRef(false);
  const isMounted = React.useRef(true);

  React.useEffect(() => () => { isMounted.current = false; }, []);

  React.useEffect(() => {
    hasFetched.current = false;
    isFetching.current = false;
    setFeatureableExercises([]);
    setAiRolePlays([]);
    setScenarios([]);
    setIsLoading(true);
  }, [$stateParams.catalogId]);

  const fetchData = React.useCallback(() => {
    if (hasFetched.current || isFetching.current) return;
    isFetching.current = true;
    setIsLoading(true);
    Promise.all([
      wrapThunkAction<CourseExercise[]>(dispatch(getFeatureableExercises($stateParams.catalogId))).catch(() => null),
      wrapThunkAction<AiRolePlay[]>(dispatch(getCourseRolePlays($stateParams.catalogId))).catch(() => null),
      wrapThunkAction<VideoPracticeScenario[]>(dispatch(getCourseScenarios($stateParams.catalogId))).catch(() => null),
    ]).then(([exercisesResult, rolePlaysResult, scenariosResult]) => {
      hasFetched.current = true;
      if (!isMounted.current) return;
      if (exercisesResult) setFeatureableExercises(exercisesResult.payload);
      if (rolePlaysResult) setAiRolePlays(rolePlaysResult.payload);
      if (scenariosResult) setScenarios(scenariosResult.payload);
    }).finally(() => {
      isFetching.current = false;
      if (isMounted.current) setIsLoading(false);
    });
  }, [$stateParams.catalogId, dispatch]);

  // When used as the mobile gallery panel, fetch immediately on mount
  React.useEffect(() => {
    if (isMobileMenu) {
      fetchData();
    }
  }, [isMobileMenu, fetchData]);

  const isSelected = $state.current?.name === 'submissions-gallery' || $state.includes('submissions-gallery');

  const handleToggle = React.useCallback((isOpen: boolean) => {
    if (!isOpen) return;
    fetchData();
  }, [fetchData]);

  const renderTarget = React.useCallback(() => (
    <> {!isMobileMenu && (
      <>
        <div className='icon-wrapper hidden-xs' role='presentation'>
          <NvIcon icon='gallery' size='small' aria-hidden='true' />
        </div>
        <span className='visible-xs'>{t.COURSE_HOME.HEADER.GALLERY()}</span>
        <div className='selected-indicator' />
      </>
    )}
    </>
  ), [isMobileMenu]);

  const items: NvDropdownOption[] = React.useMemo(() => {
    if (isLoading) {
      return [{
        type: 'custom' as const,
        customItem: (
          <div css={loadingItemStyle} className='d-flex align-items-center'>
            <LoadingSpinner />
          </div>
        ),
      }];
    }
    return [
      ...(featureableExercises.length > 0 ? [{
        type: 'accordion-header' as const,
        id: 'assignments',
        title: t.COURSE_HOME.HEADER.GALLERY_MENU.ASSIGNMENTS(),
        items: [
          {
            type: 'text' as const,
            id: 'all',
            text: t.EXERCISES.ALL_ASSIGNMENTS(),
            callback: () => $state.go('submissions-gallery', { catalogId: $stateParams.catalogId }),
          },
          ...featureableExercises.map((exercise) => ({
            type: 'text' as const,
            id: exercise.id,
            text: exercise.title,
            callback: () => $state.go('submissions-gallery', { catalogId: $stateParams.catalogId, exerciseId: exercise.id }),
          })),
        ],
      }] : []),
      ...(scenarios.length > 0 ? [{
        type: 'accordion-header' as const,
        id: 'video-practice',
        title: t.COURSE_HOME.HEADER.GALLERY_MENU.VIDEO_PRACTICE(),
        items: scenarios.map((scenario) => ({
          type: 'text' as const,
          id: scenario.id,
          text: scenario.title,
          callback: () => $state.go('practice-room-modal', { scenarioId: scenario.id }),
        })),
      }] : []),
      ...(aiRolePlays.length > 0 ? [{
        type: 'accordion-header' as const,
        id: 'ai-roleplays',
        title: t.COURSE_HOME.HEADER.GALLERY_MENU.AI_ROLEPLAYS(),
        items: aiRolePlays.map((rolePlay) => ({
          type: 'text' as const,
          id: rolePlay.id,
          text: rolePlay.title,
          callback: () => $state.go('role-play-submission-modal', { catalogId: $stateParams.catalogId, rolePlayId: rolePlay.id }),
        })),
      }] : []),
    ];
  }, [$state, $stateParams, featureableExercises, aiRolePlays, scenarios, isLoading]);

  return (
    <div
      css={styles}
      className={`page-link ${isSelected ? 'selected ' : ''}${isWhiteIcon ? 'is-white-icon' : ''}`}
    >
      <NvDropdown
        buttonStyle={NvDropdownButtonStyle.CUSTOM}
        items={items}
        align={NvDropdownAlign.RIGHT}
        noMenuPadding
        showSelectedIndicator
        isFocusable={!isEditOrReorderMode}
        onToggle={handleToggle}
        altLabel={t.COURSE_HOME.HEADER.GALLERY()}
        menuClassName='submissions-gallery-menu'
        show={isMobileMenu || undefined}
        customTarget={renderTarget}
      />
    </div>
  );
};

export default SubmissionsGalleryMenu;
